import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import formStyles from 'pages/Form.module.css';
import globalStyles from 'Global.module.css';
import { UpdateMemberProfileDocument, CreateImageUploadDocument } from 'graphql/mutations.generated';
import { GetQuestionsDocument, GetQuestionsQuery, GetCurrentMemberProfileQuery, GetCurrentMemberProfileDocument } from 'graphql/queries.generated';
import ImageUpload from 'components/ImageUpload';
import { ImageOwnerType, ImageSize } from 'app-constants';

const EditMemberProfile: React.FC = () => {
    const [username, setUsername] = useState('');
    const [bio, setBio] = useState('');
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [error, setError] = useState('');
    const [pendingImageUpload, setPendingImageUpload] = useState<{
        uploadInput: any;
        file: File;
    } | null>(null);
    const navigate = useNavigate();
    const { organizationId } = useParams<{ organizationId: string }>();

    const { data: memberData, loading: memberLoading } = useQuery<GetCurrentMemberProfileQuery>(GetCurrentMemberProfileDocument, {
        variables: {
            organizationId: organizationId || ''
        },
        skip: !organizationId
    });

    const { data: questionsData, loading: questionsLoading } = useQuery<GetQuestionsQuery>(GetQuestionsDocument, {
        variables: {
            organizationId: organizationId
        },
        skip: !organizationId
    });

    const [updateMemberProfile, { loading: updateLoading }] = useMutation(UpdateMemberProfileDocument);
    const [createImageUpload] = useMutation(CreateImageUploadDocument);

    const loading = updateLoading;

    useEffect(() => {
        if (memberData?.currentMemberProfile) {
            const member = memberData.currentMemberProfile;
            setUsername(member.groupProfile?.username || '');
            setBio(member.groupProfile?.bio || '');
            
             const initialAnswers: Record<string, string> = {};
            member.answers?.forEach(answer => {
                initialAnswers[answer.organizationMembershipQuestionId] = answer.answer;
            });
            setAnswers(initialAnswers);
        }
    }, [memberData]);

    const handleAnswerChange = (questionId: string, value: string) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!memberData?.currentMemberProfile?.organizationMemberId) {
            setError('Organization member ID is required');
            return;
        }

        try {
            let imageIdToUse = memberData?.currentMemberProfile?.groupProfile?.imageId;

            // Handle image upload if pending
            if (pendingImageUpload) {
                const uploadResult = await createImageUpload({
                    variables: {
                        ownerId: pendingImageUpload.uploadInput.ownerId,
                        ownerType: pendingImageUpload.uploadInput.ownerType,
                        contentType: pendingImageUpload.uploadInput.contentType,
                        crop_left: pendingImageUpload.uploadInput.crop_left,
                        crop_top: pendingImageUpload.uploadInput.crop_top,
                        crop_width: pendingImageUpload.uploadInput.crop_width,
                        crop_height: pendingImageUpload.uploadInput.crop_height,
                    },
                });

                if (uploadResult.data?.createImageUpload) {
                    const { imageId, uploadURL } = uploadResult.data.createImageUpload;

                    // Upload the file to S3
                    await fetch(uploadURL, {
                        method: 'PUT',
                        body: pendingImageUpload.file,
                    });

                    imageIdToUse = imageId;
                }
            }

            // Build answers array
            const answersArray = (questionsData?.questions || []).map(q => ({
                organizationMembershipQuestionId: q.organizationMembershipQuestionId,
                answer: answers[q.organizationMembershipQuestionId] || '',
            }));

            const result = await updateMemberProfile({
                variables: {
                    organizationMemberId: memberData?.currentMemberProfile?.organizationMemberId || '',
                    username,
                    bio,
                    imageId: imageIdToUse,
                    answers: answersArray,
                },
            });

            if (result.data?.updateOrganizationMemberProfile) {
                navigate(`/groups/${organizationId}`);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };

    if (memberLoading || questionsLoading) {
        return (
            <div className={`${formStyles.formContainer}`}>
                <p>Loading profile...</p>
            </div>
        );
    }

    if (!memberData?.currentMemberProfile || !memberData?.currentMemberProfile?.organizationMemberId) {
        return (
            <div className={`${formStyles.formContainer}`}>
                <p className={formStyles.error}>Member profile not found</p>
            </div>
        );
    }

    return (
        <div className={`${formStyles.formContainer}`}>
            <h1>Edit Member Profile</h1>
            <p>Here you can configure information that will only be viewable as part of your membership in this group. If you don't fill it out your default profile will be used.</p>
            <form onSubmit={handleSubmit}>
                <div>
                    <label htmlFor="username">Username:</label>
                    <input 
                        type="text" 
                        id="username" 
                        value={username} 
                        onChange={(e) => setUsername(e.target.value)} 
                    />
                </div>
                <div>
                    <label htmlFor="bio">Bio:</label>
                    <textarea
                        id="bio"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        rows={4}
                    />
                </div>
                <div>
                    <label>Profile Image:</label>
                    <ImageUpload
                        ownerType={ImageOwnerType.Person}
                        displaySize={ImageSize.Profile}
                        imageId={memberData?.currentMemberProfile?.groupProfile?.imageId}
                        ownerId={memberData?.currentMemberProfile?.personId || ''}
                        onImageChange={setPendingImageUpload}
                        aspectRatio={1}
                    />
                </div>
                
                {questionsData?.questions && questionsData.questions.length > 0 && (
                    <>
                        <h2>Membership Questions</h2>
                        {questionsData.questions.map((question) => (
                            <div key={question.organizationMembershipQuestionId}>
                                <label htmlFor={`question-${question.organizationMembershipQuestionId}`}>
                                    {question.question}
                                </label>
                                <textarea
                                    id={`question-${question.organizationMembershipQuestionId}`}
                                    value={answers[question.organizationMembershipQuestionId] || ''}
                                    onChange={(e) => handleAnswerChange(question.organizationMembershipQuestionId, e.target.value)}
                                    rows={3}
                                />
                            </div>
                        ))}
                    </>
                )}

                {error && <div className={formStyles.error}>{error}</div>}
                <button type="submit" disabled={loading} className={globalStyles.submitButton}>
                    {loading ? 'Updating profile...' : 'Update Profile'}
                </button>
            </form>
        </div>
    );
};

export default EditMemberProfile;

